import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import kitchenImg from '../assets/kitchen.jpg';
import bathroomImg from '../assets/bathroom.jpg';
import closetImg from '../assets/closet.jpg';

interface ServicesProps {
  theme?: 'dark' | 'light';
}

const Services: React.FC<ServicesProps> = ({ theme = 'dark' }) => {
  const isLight = theme === 'light';

  const services = [
    {
      id: 1,
      title: 'Kitchen Cabinetry',
      description: 'Luxury kitchens built for modern living.',
      image: kitchenImg,
      features: ['Custom layouts', 'Premium materials', 'Island design', 'Storage optimization'],
      link: '#contact'
    },
    {
      id: 2,
      title: 'Bathroom Cabinetry',
      description: 'Refined vanities designed for durability and elegance.',
      image: bathroomImg,
      features: ['Floating vanities', 'Double sink units', 'High moisture materials', 'Integrated lighting'],
      link: '#contact'
    },
    {
      id: 3,
      title: 'Walk-In Closets',
      description: 'Bespoke organization systems for sophisticated lifestyles.',
      image: closetImg,
      features: ['Custom shelving', 'Drawer systems', 'Shoe display units', 'Built-in lighting'],
      link: '#contact'
    }
  ];

  return (
    <section id="services" className="bg-white">
      <div className="grid grid-cols-1 lg:grid-cols-3 min-h-screen gap-8 bg-gray-50 p-8">
        {services.map((service, index) => (
          <motion.div
            key={service.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: index * 0.2 }}
            viewport={{ once: true }}
            className={`relative group h-[85vh] lg:h-[90vh] overflow-hidden shadow-2xl ${isLight ? 'bg-gray-100' : 'bg-primary'}`}
          >
            {/* Background Image */}
            <div className="absolute inset-0 transition-transform duration-[1500ms] group-hover:scale-110">
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-full object-cover"
              />
              <div className={`absolute inset-0 transition-colors duration-700 ${isLight ? 'bg-gray-200/90 group-hover:bg-gray-200/95' : 'bg-primary/80 group-hover:bg-primary/90'}`} />
            </div>

            {/* Content */}
            <div className={`absolute inset-0 p-10 md:p-16 flex flex-col justify-end ${isLight ? 'text-primary' : 'text-white'}`}>
              <div className="transform transition-transform duration-700 group-hover:-translate-y-6">
                <h3 className="text-5xl md:text-6xl font-bold mb-6 border-l-4 border-accent pl-6 leading-tight">
                  {service.title}
                </h3>
                <p className={`text-xl mb-8 max-w-md font-light leading-relaxed ${isLight ? 'text-gray-600' : 'text-gray-200'}`}>
                  {service.description}
                </p>
                
                <ul className="space-y-3 mb-10 opacity-0 group-hover:opacity-100 transition-opacity duration-700 delay-100 h-0 group-hover:h-auto overflow-hidden">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-base tracking-wide font-light">
                      <Check size={18} className="text-accent mr-3" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <a
                  href={service.link}
                  className="inline-flex items-center uppercase tracking-[0.2em] text-sm font-bold text-accent hover:text-accent/80 transition-colors opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 duration-500 delay-200"
                >
                  Start Project <ArrowRight size={18} className="ml-2" />
                </a>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default Services;
